/**
 * Picks signals — collects behavioural signals used to seed personalised
 * recommendations on the Picks For You page.
 *
 * Signal sources (per-browser, anonymous):
 *   - intus_recently_viewed  localStorage  (written by sections/main-product.liquid)
 *   - intus_wishlist          localStorage  (existing, written by assets/wishlist.js)
 *   - /cart.js                fetch          (current cart contents)
 *   - data-order-product-ids  attribute      (rendered server-side when customer is logged in)
 *
 * Exposed as window.PicksSignals to keep things zero-build.
 */
(function () {
  'use strict';

  const RECENTLY_VIEWED_KEY = 'intus_recently_viewed';
  const WISHLIST_KEY = 'intus_wishlist';
  const RECENTLY_VIEWED_MAX = 20;

  function safeParse(raw, fallback) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  }

  function getRecentlyViewedIds() {
    try {
      const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
      const list = safeParse(raw, []);
      return list.map((entry) => String(entry && entry.product_id ? entry.product_id : entry)).filter(Boolean);
    } catch {
      return [];
    }
  }

  function getWishlistIds() {
    try {
      const raw = localStorage.getItem(WISHLIST_KEY);
      const items = safeParse(raw, []);
      return items.map((item) => String(item && item.product_id ? item.product_id : item)).filter(Boolean);
    } catch {
      return [];
    }
  }

  let cartIdsPromise = null;
  function getCartIds() {
    if (cartIdsPromise) return cartIdsPromise;
    cartIdsPromise = fetch('/cart.js', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((cart) =>
        (cart.items || [])
          .filter((item) => {
            const title = (item.product_title || '').toLowerCase();
            return title !== 'shipping protection';
          })
          .map((item) => String(item.product_id))
      )
      .catch(() => []);
    return cartIdsPromise;
  }

  function invalidateCartIds() {
    cartIdsPromise = null;
  }

  function getOrderHistoryIds(rootEl) {
    const raw = rootEl && rootEl.dataset ? rootEl.dataset.orderProductIds : '';
    if (!raw) return [];
    return raw
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  function pushRecentlyViewed(productId, productHandle) {
    if (!productId) return;
    const id = String(productId);
    let list = [];
    try {
      list = safeParse(localStorage.getItem(RECENTLY_VIEWED_KEY), []);
    } catch {
      list = [];
    }
    const filtered = list.filter((entry) => String(entry && entry.product_id) !== id);
    filtered.unshift({ product_id: id, handle: productHandle || '', ts: Date.now() });
    const trimmed = filtered.slice(0, RECENTLY_VIEWED_MAX);
    try {
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(trimmed));
    } catch {
      /* quota / privacy mode — ignore */
    }
  }

  /**
   * Collect all signals. Returns a promise resolving to:
   *   { recentlyViewed: [], wishlist: [], cart: [], orders: [] }
   * Each list is an array of stringified product IDs ordered most-relevant first.
   */
  async function collect(rootEl) {
    const [cart] = await Promise.all([getCartIds()]);
    return {
      recentlyViewed: getRecentlyViewedIds(),
      wishlist: getWishlistIds(),
      cart,
      orders: getOrderHistoryIds(rootEl),
    };
  }

  function hashSignals(signals) {
    const flat = [
      signals.orders.join(','),
      signals.wishlist.join(','),
      signals.cart.join(','),
      signals.recentlyViewed.join(','),
    ].join('|');
    let h = 0;
    for (let i = 0; i < flat.length; i += 1) {
      h = (h << 5) - h + flat.charCodeAt(i);
      h |= 0;
    }
    return String(h);
  }

  window.PicksSignals = {
    collect,
    hashSignals,
    pushRecentlyViewed,
    getRecentlyViewedIds,
    getWishlistIds,
    getOrderHistoryIds,
    invalidateCartIds,
    RECENTLY_VIEWED_KEY,
  };

  document.addEventListener('cart:updated', invalidateCartIds);
})();
